/** True when variant label is the generic «all sizes» bucket (not a distinct price tier). */
export function isAllSizesVariantLabel(variant) {
  const v = String(variant || "").trim();
  return v === "جميع الأحجام" || v.startsWith("جميع الأحجام - ");
}

function hasVariantChoice(size, color, variant) {
  if (String(size || "").trim()) return true;
  if (String(color || "").trim()) return true;
  const v = String(variant || "").trim();
  return Boolean(v && v !== "الافتراضي");
}

/**
 * Remove «جميع الأحجام» / «All sizes» from product title when a specific size/color was chosen.
 */
export function stripAllSizesFromProductTitle(name, size, color, variant) {
  let n = String(name || "").trim();
  if (!hasVariantChoice(size, color, variant)) return n;

  const patterns = [
    /\s*[—–-]\s*جميع الأحجام(?:\s*[—–-]\s*[^—–-]+)?\s*$/u,
    /\s*جميع الأحجام\s*$/u,
    /\s*[—–-]\s*All\s+sizes(?:\s*[—–-]\s*[^—–-]+)?\s*$/i,
    /\s*All\s+sizes\s*$/i,
  ];
  for (const re of patterns) {
    n = n.replace(re, "").trim();
  }
  return n;
}

/**
 * Whether to show «جميع الأحجام» as a separate variant label.
 * Hide when size/color already specify the line (same price across sizes).
 */
export function shouldShowAllSizesVariant(variant, size, color) {
  const v = String(variant || "").trim();
  if (!v || v === "الافتراضي") return false;
  if (!isAllSizesVariantLabel(v)) return true;
  const hasSize = Boolean(String(size || "").trim());
  const hasColor = Boolean(String(color || "").trim());
  return !hasSize && !hasColor;
}

function splitVariantParts(label) {
  return String(label || "")
    .split(" - ")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** «3 لتر - 3 لتر» → «3 لتر» (price tier whose description repeats the size). */
export function dedupeVariantLabel(label) {
  const out = [];
  for (const p of splitVariantParts(label)) {
    if (!out.includes(p)) out.push(p);
  }
  return out.join(" - ");
}

/** Variant text for order lines without the parts already shown as size / color meta. */
export function variantLabelForOrderLine(variant, size, color) {
  const skip = new Set([String(size || "").trim(), String(color || "").trim()].filter(Boolean));
  return splitVariantParts(dedupeVariantLabel(variant))
    .filter((p) => !skip.has(p))
    .join(" - ");
}

/** Product title + optional variant suffix for order lines (retail dashboard). */
export function formatOrderItemLineLabel({
  productName,
  selectedVariant,
  selectedSize,
  selectedColor,
}) {
  const size = String(selectedSize || "").trim();
  const color = String(selectedColor || "").trim();
  const variant = String(selectedVariant || "").trim();
  let name = stripAllSizesFromProductTitle(productName, size, color, variant);

  if (shouldShowAllSizesVariant(variant, size, color)) {
    if (variant.startsWith("جميع الأحجام - ")) {
      const suffix = variant.slice("جميع الأحجام - ".length).trim();
      if (suffix && !size && !color) name = `${name} — ${suffix}`;
    } else {
      const shown = variantLabelForOrderLine(variant, size, color);
      if (shown) name = `${name} — ${shown}`;
    }
  }

  return name;
}

/** Size / color line for order item meta (under product name). */
export function formatOrderItemVariantMeta(it, language = "ar") {
  const ar = language === "ar";
  const sizeTxt = String(it?.selected_size || "").trim();
  const colorTxt = String(it?.selected_color || "").trim();
  const parts = [];
  if (sizeTxt) parts.push(ar ? `الحجم: ${sizeTxt}` : `Size: ${sizeTxt}`);
  if (colorTxt) parts.push(ar ? `اللون: ${colorTxt}` : `Color: ${colorTxt}`);
  return parts.join(ar ? " · " : " · ");
}
